import styled from '@emotion/styled';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography
} from '@material-ui/core';
import {
  DigestFrequency,
  IIssueDigestPreference
} from '@dadohr/shared/domainTypes/adminNotificationPreferences';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { NoAccess } from '../../../../components/NoAccess';
import { useTranslation } from '../../../../i18next';
import { GridHalfSized } from '../../../../layout/Grid';
import { PageBody } from '../../../../layout/PageBody';
import { PageToolbar, PageToolbarTitle } from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes } from '../../../../routes';
import {
  saveIssueDigestPreference,
  useIssueDigestPreference
} from '../../../../services/adminNotificationPreferences';
import { useCurrentUser } from '../../../../services/currentUser';
import { useActiveExperienceTemplatesCurrentUserCanSee } from '../../../../services/experiences/template';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { getTzAbbr } from '../../../../services/time';
import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { useOrgSideNav } from '../sideNav';

// Red-outlined destructive button. `&&` beats MUI v4's JSS specificity.
const DangerOutlinedButton = styled(ButtonWithPromise)`
  && {
    color: ${(p) => p.theme.palette.error.main};
    border-color: ${(p) => p.theme.palette.error.main};
  }
  &&:hover {
    border-color: ${(p) => p.theme.palette.error.main};
    background-color: rgba(211, 47, 47, 0.04);
  }
`;

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' }
];

const formatTimeLabel = (hour: number, minute: number): string => {
  const period = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
};

// 30-minute increments across the day.
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? 0 : 30;
  return { value: `${hour}:${minute}`, label: formatTimeLabel(hour, minute), hour, minute };
});

const DEFAULT_TIME = '9:0'; // 09:00 AM

const ContentOrgNotificationEmails = ({ currentUser }: { currentUser: ICurrentUser }) => {
  const { t } = useTranslation();
  const sideNav = useOrgSideNav();

  const [templates, loadingTemplates] = useActiveExperienceTemplatesCurrentUserCanSee(currentUser);
  const [preferenceDoc, loadingPreference] = useIssueDigestPreference(
    currentUser.id,
    currentUser.org.id
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<DigestFrequency>('DAILY_WEEKDAYS');
  const [weeklyDay, setWeeklyDay] = useState<number>(1);
  const [timeValue, setTimeValue] = useState<string>(DEFAULT_TIME);

  // Seed the form once from the saved preference (avoid clobbering edits on
  // subsequent listener emissions).
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    if (loadingPreference || loadingTemplates) return;

    const digest = preferenceDoc?.data.issueDigest;
    if (digest) {
      setFrequency(digest.frequency);
      setWeeklyDay(digest.weeklyDay ?? 1);
      setTimeValue(`${digest.sendHour}:${digest.sendMinute ?? 0}`);
      setSelectedIds(
        digest.templateSelection === 'ALL'
          ? (templates ?? []).map((tpl) => tpl.id)
          : digest.templateSelection
      );
    }
    seeded.current = true;
  }, [loadingPreference, loadingTemplates, preferenceDoc, templates]);

  const toggleTemplate = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const buildDigest = (enabled: boolean, templateSelection: string[]): IIssueDigestPreference => {
    const [hourStr, minuteStr] = timeValue.split(':');
    return {
      enabled,
      templateSelection,
      frequency,
      weeklyDay: frequency === 'WEEKLY' ? (weeklyDay as IIssueDigestPreference['weeklyDay']) : null,
      sendHour: Number(hourStr),
      sendMinute: Number(minuteStr),
      channel: 'email'
    };
  };

  const handleUpdate = async () => {
    // Enabled only if at least one experience is selected.
    await saveIssueDigestPreference(buildDigest(selectedIds.length > 0, selectedIds));
  };

  const handleStopAll = async () => {
    // Disable and clear the selection so the UI and stored preference stay in sync.
    await saveIssueDigestPreference(buildDigest(false, []));
    setSelectedIds([]);
  };

  const templateLabel = (tpl: { data: { adminTitle?: string; title?: string } }) =>
    tpl.data.adminTitle || tpl.data.title || 'Untitled experience';

  const activeTemplates = useMemo(
    () =>
      [...(templates ?? [])].sort((a, b) =>
        templateLabel(a).localeCompare(templateLabel(b), undefined, { sensitivity: 'base' })
      ),
    [templates]
  );
  const loading = loadingTemplates || loadingPreference;

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('pageTitle.orgNotificationEmails')}
          </Typography>
        </PageToolbarTitle>
      </PageToolbar>

      <GridHalfSized>
        <div>
          <Section spacing={2}>
            <Typography variant="body1">
              Emails notifying you of issues with your active experiences, such as email bounces,
              missing data and issues with automations.
            </Typography>
          </Section>

          {loading ? (
            <Section spacing={2}>
              <Typography variant="body2">Loading…</Typography>
            </Section>
          ) : (
            <>
              <Section spacing={2}>
                <Typography variant="h4">Experiences</Typography>
                <Typography variant="body2" style={{ marginTop: 4 }}>
                  You'll receive one email digest that includes a summary of issues across the
                  experiences you select below.
                </Typography>
              </Section>

              <Section spacing={4}>
                {activeTemplates.length === 0 ? (
                  <Typography variant="body2">No active experiences.</Typography>
                ) : (
                  <FormGroup>
                    {activeTemplates.map((tpl) => (
                      <FormControlLabel
                        key={tpl.id}
                        control={
                          <Checkbox
                            checked={selectedIds.includes(tpl.id)}
                            onChange={() => toggleTemplate(tpl.id)}
                            color="primary"
                          />
                        }
                        label={templateLabel(tpl)}
                      />
                    ))}
                  </FormGroup>
                )}
              </Section>

              <Section spacing={2}>
                <Typography variant="h4">Frequency</Typography>
                <FormControl component="fieldset" style={{ marginTop: 8 }}>
                  <RadioGroup
                    row
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value as DigestFrequency)}
                  >
                    <FormControlLabel
                      value="DAILY_WEEKDAYS"
                      control={<Radio color="primary" />}
                      label="Daily (Mon - Fri)"
                    />
                    <FormControlLabel
                      value="WEEKLY"
                      control={<Radio color="primary" />}
                      label="Weekly"
                    />
                  </RadioGroup>
                </FormControl>
              </Section>

              {frequency === 'WEEKLY' && (
                <Section spacing={2}>
                  <FormControl>
                    <FormLabel style={{ marginBottom: 4 }}>Day</FormLabel>
                    <Select
                      value={weeklyDay}
                      onChange={(e) => setWeeklyDay(Number(e.target.value))}
                      style={{ minWidth: 180 }}
                    >
                      {WEEKDAYS.map((d) => (
                        <MenuItem key={d.value} value={d.value}>
                          {d.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Section>
              )}

              <Section spacing={4}>
                <FormControl>
                  <FormLabel style={{ marginBottom: 4 }}>
                    Time ({getTzAbbr(currentUser.org.tz)})
                  </FormLabel>
                  <Select
                    value={timeValue}
                    onChange={(e) => setTimeValue(String(e.target.value))}
                    style={{ minWidth: 180 }}
                  >
                    {TIME_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Section>

              <Section spacing={2}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <ButtonWithPromise
                    variant="contained"
                    color="primary"
                    onClick={handleUpdate}
                    pending={t('Applying')}
                  >
                    Update
                  </ButtonWithPromise>
                  <DangerOutlinedButton
                    variant="outlined"
                    onClick={handleStopAll}
                    pending={t('Applying')}
                  >
                    Stop all notifications
                  </DangerOutlinedButton>
                </div>
              </Section>
            </>
          )}
        </div>
      </GridHalfSized>
    </PageBody>
  );
};

export const PageOrgNotificationEmails = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.org.notificationEmails)) {
    return <NoAccess />;
  }

  return <ContentOrgNotificationEmails currentUser={currentUser} />;
};
